/**
 * Una fila de opciones de las que se elige una (los «chips» del diseño: 10% · 15% · 20%…). Son radios de verdad,
 * escondidos detrás de su etiqueta: se eligen con el mouse, con Tab y las flechas, y el lector de pantalla los lee
 * como un grupo con su nombre. Cada opción mide al menos 44 px de alto.
 */

import type { ReactNode } from "react";

export function Opciones<T extends string | number>({
  nombre,
  leyenda,
  opciones,
  valor,
  onCambio,
  etiqueta = (o) => String(o),
  ariaLabel,
  ayudaId,
  deshabilitada,
  invalida,
}: {
  /** el `name` de los radios: único en la pantalla */
  nombre: string;
  leyenda: ReactNode;
  opciones: readonly T[];
  valor: T | null;
  onCambio: (o: T) => void;
  etiqueta?: (o: T) => ReactNode;
  /** para opciones sin texto (un color): el nombre que lee el lector de pantalla */
  ariaLabel?: (o: T) => string;
  ayudaId?: string;
  deshabilitada?: (o: T) => boolean;
  invalida?: boolean;
}) {
  return (
    <fieldset aria-describedby={ayudaId} aria-invalid={invalida || undefined}>
      <legend className="text-sm font-semibold text-slate-900">{leyenda}</legend>
      <div className="mt-2 flex flex-wrap gap-2">
        {opciones.map((o) => {
          const elegida = o === valor;
          const apagada = deshabilitada?.(o) ?? false;
          return (
            <label
              key={String(o)}
              className={`inline-flex min-h-[44px] min-w-[56px] items-center justify-center rounded-xl border px-4 text-sm font-semibold focus-within:ring-2 focus-within:ring-violet-400 ${
                apagada
                  ? "cursor-not-allowed border-slate-100 bg-slate-50 text-slate-400"
                  : elegida
                    ? "cursor-pointer border-violet-600 bg-violet-600 text-white"
                    : "cursor-pointer border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
              }`}
            >
              <input
                type="radio"
                name={nombre}
                value={String(o)}
                checked={elegida}
                disabled={apagada}
                onChange={() => onCambio(o)}
                aria-label={ariaLabel?.(o)}
                className="sr-only"
              />
              {etiqueta(o)}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
